import { useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import { answerText } from '@mds/shared/lib/form-answers';
import { cn } from '@mds/shared/lib/utils';
import type {
  AdminBookingFeedbackResponse,
  BookingFeedbackFormKind,
} from '@mds/shared/types/booking-feedback';
import { FORM_RATING_MAX } from '@mds/shared/types/form-question';
import Select from '@mds/shared/ui/select';

import * as feedback from '@/bookings/booking-feedback.api';
import BarRanking from '@/components/charts/bar-ranking';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Pager from '@/components/ui/pager';
import StatCard from '@/components/ui/stat-card';
import { Cell } from '@/components/ui/table';
import { FORM_KIND_LABELS, bookingDatesLabel } from '@/domain/booking-feedback';

const PAGE_SIZE = 20;
const ALL = '';

const RATING_OPTIONS = [
  { value: ALL, label: 'Any rating' },
  ...Array.from({ length: FORM_RATING_MAX }, (_, i) => {
    const rating = FORM_RATING_MAX - i;
    return { value: String(rating), label: `${rating} / ${FORM_RATING_MAX}` };
  }),
];

/**
 * What clients said about the spaces they booked, with who said it: booking feedback is not
 * anonymous, so a low rating can be followed up. The figures are the server's, over the kind and
 * space picked; the rating filter narrows the list only.
 */
export default function BookingFeedbackResults() {
  const [kind, setKind] = useState<BookingFeedbackFormKind | typeof ALL>(ALL);
  const [resourceId, setResourceId] = useState(ALL);
  const [rating, setRating] = useState(ALL);
  const [direction, setDirection] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const query = {
    page,
    limit: PAGE_SIZE,
    direction,
    ...(kind ? { kind } : {}),
    ...(resourceId ? { resourceId } : {}),
    ...(rating ? { rating: Number(rating) } : {}),
  };
  const list = useQuery({
    queryKey: ['booking-feedback-responses', query],
    queryFn: () => feedback.listResponses(query),
    placeholderData: keepPreviousData,
  });

  /** Any filter change starts the list again from the top, closed. */
  const refilter = (apply: () => void) => {
    apply();
    setPage(1);
    setOpenId(null);
  };

  if (list.isPending) return <p className="m-0 text-base text-ink-muted">Loading responses…</p>;
  if (list.isError)
    return (
      <EmptyState
        title="Could not load responses"
        hint={
          list.error instanceof ApiError ? list.error.message : 'The server could not be reached.'
        }
      />
    );

  const { summary, data: rows, pagination, spaces } = list.data;
  const spaceOptions = [
    { value: ALL, label: 'Every space' },
    ...spaces
      .filter((space) => !kind || space.kind === kind)
      .map((space) => ({ value: space.id, label: space.name })),
  ];
  const distribution = Array.from({ length: FORM_RATING_MAX }, (_, i) => {
    const value = FORM_RATING_MAX - i;
    const count = summary.ratingDistribution[String(value)] ?? 0;
    return {
      key: String(value),
      label: `${value} / ${FORM_RATING_MAX}`,
      value: count,
      display: String(count),
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={kind}
          onChange={(v) =>
            refilter(() => {
              setKind(v as BookingFeedbackFormKind | typeof ALL);
              setResourceId(ALL);
            })
          }
          options={[
            { value: ALL, label: 'Desks and rooms' },
            { value: 'desk', label: FORM_KIND_LABELS.desk },
            { value: 'space', label: FORM_KIND_LABELS.space },
          ]}
          aria-label="Booking kind"
        />
        <Select
          value={resourceId}
          onChange={(v) => refilter(() => setResourceId(v))}
          options={spaceOptions}
          aria-label="Space"
        />
        <Select
          value={rating}
          onChange={(v) => refilter(() => setRating(v))}
          options={RATING_OPTIONS}
          aria-label="Rating"
        />
        <Select
          value={direction}
          onChange={(v) => refilter(() => setDirection(v as 'desc' | 'asc'))}
          options={[
            { value: 'desc', label: 'Newest first' },
            { value: 'asc', label: 'Oldest first' },
          ]}
          aria-label="Order responses"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Responses"
          value={String(summary.totalResponses)}
          note={`from ${summary.invitations} ${summary.invitations === 1 ? 'client' : 'clients'} asked`}
          density="compact"
        />
        <StatCard
          label="Response rate"
          value={`${Math.round(summary.responseRatePercent)}%`}
          note="of the clients asked"
          density="compact"
        />
        <StatCard
          label="Average rating"
          value={
            summary.averageOverallRating === null ? '–' : summary.averageOverallRating.toFixed(1)
          }
          note={`out of ${FORM_RATING_MAX}`}
          density="compact"
        />
      </div>

      {summary.totalResponses > 0 && (
        <Card title="Overall rating">
          <BarRanking rows={distribution} />
        </Card>
      )}

      <Card title="Responses">
        {rows.length === 0 ? (
          <EmptyState
            title={summary.totalResponses === 0 ? 'No responses yet' : 'No responses match'}
            hint={
              summary.totalResponses === 0
                ? 'Clients are asked once a booking ends, the first time they book each space, while that form is switched on.'
                : 'Try another rating.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-base">
                <thead>
                  <tr>
                    {['Submitted', 'Client', 'Space', 'Rating', 'Comment'].map((h) => (
                      <th
                        key={h}
                        className={cn(
                          'border-b-2 border-hairline pr-4 pb-3 font-mono text-sm font-semibold tracking-wide text-ink-secondary uppercase',
                          h === 'Comment' && 'hidden lg:table-cell',
                        )}
                      >
                        {h}
                      </th>
                    ))}
                    <th className="border-b-2 border-hairline pb-3">
                      <span className="sr-only">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <ResponseRow
                      key={r.id}
                      response={r}
                      open={openId === r.id}
                      onToggle={() => setOpenId((id) => (id === r.id ? null : r.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              pagination={pagination}
              onPage={(p) => {
                setPage(p);
                setOpenId(null);
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * One response as a summary row that opens, in place, onto the client's contact details, the
 * booking, the comment and every question with its answer.
 */
function ResponseRow({
  response: r,
  open,
  onToggle,
}: {
  response: AdminBookingFeedbackResponse;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `booking-feedback-response-${r.id}`;

  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer border-b border-hairline/70 transition-colors hover:bg-brand-wash',
          open && 'border-b-0 bg-brand-wash',
        )}
      >
        <Cell className="font-mono whitespace-nowrap text-ink-secondary">
          {stamp(r.submittedAt)}
        </Cell>
        <Cell className="whitespace-nowrap">{r.customer.name}</Cell>
        <Cell className="whitespace-nowrap text-ink-secondary">{r.space.name}</Cell>
        <Cell>
          <Chip
            className={cn(
              'border',
              r.overallRating <= 2
                ? 'border-danger-edge bg-danger-wash text-danger-text'
                : 'border-brand-edge/35 bg-brand-wash text-brand-strong',
            )}
          >
            {r.overallRating} / {FORM_RATING_MAX}
          </Chip>
        </Cell>
        <Cell className="hidden w-full max-w-0 lg:table-cell">
          {r.comment ? (
            <span className="block truncate">{r.comment}</span>
          ) : (
            <span className="text-ink-muted">No comment</span>
          )}
        </Cell>
        <Cell className="pr-0 text-right">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? 'Hide response' : 'Show response'}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="inline-flex min-h-(--target-min) min-w-(--target-min) items-center justify-center rounded-xl text-ink-muted hover:text-brand-strong focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
          >
            <ChevronDownIcon
              aria-hidden
              className={cn('size-5 transition-transform', open && 'rotate-180')}
            />
          </button>
        </Cell>
      </tr>
      {open && (
        <tr id={panelId} className="border-b border-hairline/70 bg-brand-wash">
          <td colSpan={6} className="px-3 pb-4">
            <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-5">
              <section className="grid gap-4 sm:grid-cols-2">
                <Detail title="Client">
                  <p className="m-0 font-semibold">{r.customer.name}</p>
                  <a className="text-brand-text underline" href={`mailto:${r.customer.email}`}>
                    {r.customer.email}
                  </a>
                  {r.customer.phone && <p className="m-0 text-ink-secondary">{r.customer.phone}</p>}
                </Detail>
                <Detail title="Booking">
                  <p className="m-0 font-mono">{r.booking.reference}</p>
                  <p className="m-0 text-ink-secondary">
                    {r.booking.itemName} · {r.space.name}
                  </p>
                  <p className="m-0 text-ink-secondary">
                    {bookingDatesLabel(r.booking.bookingDate, r.booking.bookingEndDate)}
                  </p>
                </Detail>
              </section>

              <Detail title="Comment" divided>
                {r.comment ? (
                  <p className="m-0 text-base whitespace-pre-line">{r.comment}</p>
                ) : (
                  <p className="m-0 text-base text-ink-muted">The client left no comment.</p>
                )}
              </Detail>

              {r.answers.length > 0 && (
                <Detail title="Questions" divided>
                  <ol className="m-0 flex list-none flex-col p-0">
                    {r.answers.map((a, i) => {
                      const text = answerText(a.value);
                      return (
                        <li
                          key={a.questionId}
                          className="grid gap-x-6 gap-y-1 border-b border-hairline/70 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]"
                        >
                          <span className="flex gap-3 text-ink-secondary">
                            <span className="font-mono text-ink-muted">{i + 1}.</span>
                            {a.label}
                          </span>
                          <span
                            className={cn(
                              'break-words whitespace-pre-line',
                              text ? 'font-semibold' : 'text-ink-muted',
                            )}
                          >
                            {text || 'Not answered'}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </Detail>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({
  title,
  divided = false,
  children,
}: {
  title: string;
  /** A rule above, for the blocks after the first row. */
  divided?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={cn('flex flex-col gap-1.5', divided && 'border-t border-hairline pt-4')}>
      <h4 className="m-0 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        {title}
      </h4>
      {children}
    </section>
  );
}
